<template>
  <div class="w-full">
    <div class="bg-white border min-h-[260px] border-gray-300 relative p-4">
      <div
        class="pt-3 pb-1.5 border-b border-b-gray-200 border-dashed flex items-center gap-3 relative"
      >
        <label
          for="ssid"
          class="font-normal w-52 text-gray-400 text-[14px] required"
          >{{ dataLocales.data.ssid }} (SSID)
        </label>
        <input
          type="text"
          id="ssid"
          v-model="qrcreate.wifi.ssid"
          class="h-[22px] border-none shadow-none placeholder-gray-300 outline-none focus:outline-none w-full"
        />
      </div>
      <div
        class="pt-3 pb-1.5 border-b border-b-gray-200 border-dashed flex items-center gap-3 relative"
      >
        <label for="password" class="font-normal w-52 text-gray-400 text-[14px]"
          >{{ dataLocales.data.password }}
        </label>
        <input
          v-model="qrcreate.wifi.password"
          id="password"
          type="text"
          class="h-[22px] border-none shadow-none placeholder-gray-300 outline-none focus:outline-none w-full"
        />
      </div>

      <div
        class="pt-3 pb-1.5 border-b border-b-gray-200 border-dashed flex items-center gap-3 relative"
      >
        <label for="hidden" class="font-normal text-gray-400 mr-6 text-[14px]"
          >{{ dataLocales.data.hidden }}
        </label>
        <Switch
          id="hidden"
          v-model="qrcreate.wifi.hidden"
          :class="qrcreate.wifi.hidden ? 'bg-success' : 'bg-success-light'"
          class="relative inline-flex h-5 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
        >
          <span class="sr-only">Is Hidden</span>
          <span
            aria-hidden="true"
            :class="qrcreate.wifi.hidden ? 'translate-x-6' : 'translate-x-0'"
            class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out"
          />
        </Switch>
      </div>
      <div
        class="pt-3 pb-1.5 border-b border-b-gray-200 border-dashed flex items-center gap-3 relative"
      >
        <label for="encrypt" class="font-normal w-52 text-gray-400 text-[14px]"
          >{{ dataLocales.data.encrypt }}
        </label>
        <Listbox v-model="qrcreate.wifi.encryption" id="encrypt">
          <div class="relative w-full">
            <ListboxButton
              class="relative w-full cursor-pointer h-[22px] text-left"
            >
              {{ qrcreate.wifi.encryption }}
            </ListboxButton>

            <transition
              leave-active-class="transition duration-100 ease-in"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <ListboxOptions
                class="absolute z-10 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              >
                <ListboxOption
                  v-slot="{ active, selected }"
                  v-for="encrypt in encryptions"
                  :value="encrypt"
                  as="template"
                >
                  <li
                    :class="[
                      active ? 'bg-success/10 text-success' : 'text-gray-900',
                      'relative cursor-default select-none py-2 pl-10 pr-4',
                    ]"
                  >
                    <span
                      :class="[
                        selected ? 'font-medium' : 'font-normal',
                        'block truncate',
                      ]"
                      >{{ encrypt }}</span
                    >
                    <span
                      v-if="selected"
                      class="absolute inset-y-0 left-0 flex items-center pl-3 text-success"
                    >
                      <CheckIcon class="h-5 w-5" aria-hidden="true" />
                    </span>
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </transition>
          </div>
        </Listbox>
      </div>
    </div>
    <ToolsButtonCreate @generate="generate" />
  </div>
</template>
<script lang="ts" setup>
import { useQrCreateStore } from "~/stores/qrcreate";
import DataLocale from "~~/config/interface/DataLocale";
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
  Switch,
} from "@headlessui/vue";
import { CheckIcon } from "vue-tabler-icons";

const qrcreate = useQrCreateStore();
const qrstyle = useQrStyle();
const encryptions = ref(["None", "WPA/WPA2", "WEP"]);

const validateData = () => {
  if (qrcreate.wifi.ssid) {
    return true;
  }
  return false;
};
const generate = async () => {
  if (validateData()) {
    await qrstyle.GenerateQrCode({ stack: true });
  }
};

const dataLocales = new DataLocale([
  {
    locale: "en",
    ssid: "Network name",
    password: "Password",
    hidden: "Hidden Wifi Network",
    encrypt: "Encryption",
  },
  {
    locale: "vi",
    ssid: "Tên mạng",
    password: "Mật khẩu",
    hidden: "Mạng Wifi Ẩn",
    encrypt: "Mã hóa",
  },

  {
    locale: "fr",
    ssid: "Nom du réseau",
    password: "Mot de passe",
    hidden: "Réseau Wifi caché",
    encrypt: "Chiffrement",
  },
  {
    locale: "it",
    ssid: "Nome della rete",
    password: "Password",
    hidden: "Rete Wifi nascosta",
    encrypt: "Crittografia",
  },
  {
    locale: "es",
    ssid: "Nombre de la red",
    password: "Contraseña",
    hidden: "Red Wifi oculta",
    encrypt: "Cifrado",
  },
  {
    locale: "pt",
    ssid: "Nome da rede",
    password: "Senha",
    hidden: "Rede Wifi oculta",
    encrypt: "Criptografia",
  },
  {
    locale: "nl",
    ssid: "Netwerknaam",
    password: "Wachtwoord",
    hidden: "Verborgen Wifi-netwerk",
    encrypt: "Versleuteling",
  },
  {
    locale: "zh",
    ssid: "网络名称",
    password: "密码",
    hidden: "隐藏的Wifi网络",
    encrypt: "加密",
  },
  {
    locale: "ja",
    ssid: "ネットワーク名",
    password: "パスワード",
    hidden: "非表示のWifiネットワーク",
    encrypt: "暗号化",
  },
  {
    locale: "de",
    ssid: "Netzwerkname",
    password: "Passwort",
    hidden: "Verstecktes WLAN-Netzwerk",
    encrypt: "Verschlüsselung",
  },
]);
</script>
